import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertCircle, ArrowUpRight, CalendarDays, Clock, GraduationCap, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card } from "@/platform/ui/card";
import { Input } from "@/platform/ui/input";
import { Label } from "@/platform/ui/label";
import { useSession } from "@/platform/auth/use-session";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { TrophySpot } from "@/platform/ui/illustrations/spot";
import { completeUspOnboarding, fetchUspProfile } from "@/tenants/usp/features/profile/api";
import type { UspCurso } from "@/tenants/usp/lib/nota-projetada-fuvest";

const CURSO_OPTIONS: Array<{ value: UspCurso; label: string }> = [
  { value: "engenharia_producao", label: "Engenharia de Produção (Poli)" },
  { value: "engenharia_civil", label: "Engenharia Civil (Poli)" },
  { value: "engenharia_eletrica", label: "Engenharia Elétrica (Poli)" },
  { value: "engenharia_mecanica", label: "Engenharia Mecânica (Poli)" },
  { value: "engenharia_quimica", label: "Engenharia Química (Poli)" },
  { value: "computacao", label: "Computação / Engenharia da Computação" },
  { value: "medicina", label: "Medicina" },
  { value: "direito", label: "Direito" },
  { value: "economia", label: "Economia / FEA" },
  { value: "outro", label: "Outro curso USP" },
];

const onboardingSchema = z.object({
  curso_alvo: z.enum([
    "engenharia_producao",
    "engenharia_civil",
    "engenharia_eletrica",
    "engenharia_mecanica",
    "engenharia_quimica",
    "computacao",
    "medicina",
    "direito",
    "economia",
    "outro",
  ]),
  exam_date_fase1: z.string().min(10, "Data da 1ª fase obrigatória"),
  exam_date_fase2_dia1: z.string().min(10, "Data do 1º dia da 2ª fase obrigatória"),
  exam_date_fase2_dia2: z.string().min(10, "Data do 2º dia da 2ª fase obrigatória"),
  weekly_hours: z.coerce.number().min(2, "Mínimo 2h").max(80, "Máximo 80h"),
  treineira: z.boolean(),
});

type FormValues = z.infer<typeof onboardingSchema>;

// Defaults baseados no calendário Fuvest histórico (3º domingo de novembro 1ª fase)
const FUVEST_2027_FASE1 = "2026-11-22";
const FUVEST_2027_DIA1 = "2027-01-10";
const FUVEST_2027_DIA2 = "2027-01-11";

export function UspOnboardingPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      curso_alvo: "engenharia_producao",
      exam_date_fase1: FUVEST_2027_FASE1,
      exam_date_fase2_dia1: FUVEST_2027_DIA1,
      exam_date_fase2_dia2: FUVEST_2027_DIA2,
      weekly_hours: 20,
      treineira: false,
    },
  });

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      const p = await fetchUspProfile(session.user.id);
      if (cancelled || !p) return;
      if (p.curso_alvo) form.setValue("curso_alvo", p.curso_alvo);
      if (p.exam_date_fase1) form.setValue("exam_date_fase1", p.exam_date_fase1);
      if (p.exam_date_fase2_dia1) form.setValue("exam_date_fase2_dia1", p.exam_date_fase2_dia1);
      if (p.exam_date_fase2_dia2) form.setValue("exam_date_fase2_dia2", p.exam_date_fase2_dia2);
      if (p.weekly_hours != null) form.setValue("weekly_hours", Number(p.weekly_hours));
      if (p.treineira != null) form.setValue("treineira", p.treineira);
    })();
    return () => {
      cancelled = true;
    };
  }, [session, form]);

  async function onSubmit(values: FormValues) {
    if (!session) {
      toast.error("Sessão expirada. Faça login novamente.");
      navigate("/entrar", { replace: true });
      return;
    }
    setSubmitting(true);
    try {
      await completeUspOnboarding(session.user.id, values);
      toast.success("Pronto. Bem-vinda ao FuvestPro USP.");
      navigate("/usp/painel", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível salvar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
      <Blob color="mint" className="pointer-events-none absolute -right-32 bottom-0 w-96 opacity-25 blur-2xl animate-blob" />
      <SoftStar color="yellow" className="pointer-events-none absolute right-12 top-24 w-14 opacity-60 animate-float" />

      <Container size="default" className="relative py-12 md:py-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-start">
          {/* Hero side */}
          <div className="animate-fade-up">
            <TrophySpot className="size-20" />
            <Badge variant="accentSolid" className="mt-4 gap-2">
              <Sparkles className="size-3" />
              FuvestPro USP · onboarding
            </Badge>
            <h1 className="mt-5 font-display text-[length:var(--text-display-lg)] font-semibold leading-tight tracking-[var(--text-display-lg--letter-spacing)] text-balance">
              Vamos calibrar o seu plano Fuvest.
            </h1>
            <p className="mt-4 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-relaxed text-pretty">
              Três informações decidem o restante: o curso-alvo (define os pesos), as datas do
              vestibular (mede tempo restante) e a carga horária semanal disponível.
            </p>

            <ul className="mt-7 space-y-3 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              <li className="flex gap-2.5">
                <GraduationCap className="mt-0.5 size-4 shrink-0" style={{ color: "var(--color-accent-deep)" }} />
                <span>Pesos por curso USP — Eng. Produção tem Mat × 4, Fís × 3, Quím × 2.</span>
              </li>
              <li className="flex gap-2.5">
                <CalendarDays className="mt-0.5 size-4 shrink-0" style={{ color: "var(--color-accent-deep)" }} />
                <span>Calendário Fuvest 2027 (provisório) já preenchido — ajuste conforme edital oficial.</span>
              </li>
              <li className="flex gap-2.5">
                <Clock className="mt-0.5 size-4 shrink-0" style={{ color: "var(--color-accent-deep)" }} />
                <span>Plano semanal adaptativo recalibra automaticamente conforme você avança.</span>
              </li>
            </ul>
          </div>

          {/* Form side */}
          <Card className="p-7 md:p-10">
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
              <div>
                <Label htmlFor="curso_alvo">Curso-alvo na USP</Label>
                <select
                  id="curso_alvo"
                  {...form.register("curso_alvo")}
                  className="mt-2 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3.5 py-2.5 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
                >
                  {CURSO_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                  Define a ponderação Fuvest. Pesos detalhados em /usp/banca-fuvest.
                </p>
              </div>

              <div>
                <Label htmlFor="exam_date_fase1">Data da 1ª fase Fuvest</Label>
                <Input
                  id="exam_date_fase1"
                  type="date"
                  {...form.register("exam_date_fase1")}
                  className="mt-2"
                />
                {form.formState.errors.exam_date_fase1 && (
                  <Field error={form.formState.errors.exam_date_fase1.message} />
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="exam_date_fase2_dia1">2ª fase · 1º dia</Label>
                  <Input
                    id="exam_date_fase2_dia1"
                    type="date"
                    {...form.register("exam_date_fase2_dia1")}
                    className="mt-2"
                  />
                  {form.formState.errors.exam_date_fase2_dia1 && (
                    <Field error={form.formState.errors.exam_date_fase2_dia1.message} />
                  )}
                </div>
                <div>
                  <Label htmlFor="exam_date_fase2_dia2">2ª fase · 2º dia</Label>
                  <Input
                    id="exam_date_fase2_dia2"
                    type="date"
                    {...form.register("exam_date_fase2_dia2")}
                    className="mt-2"
                  />
                  {form.formState.errors.exam_date_fase2_dia2 && (
                    <Field error={form.formState.errors.exam_date_fase2_dia2.message} />
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="weekly_hours">Horas semanais disponíveis</Label>
                <Input
                  id="weekly_hours"
                  type="number"
                  min={2}
                  max={80}
                  step={1}
                  {...form.register("weekly_hours", { valueAsNumber: true })}
                  className="mt-2"
                />
                <p className="mt-1.5 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                  Realista. Não precisa exagerar. Recomendamos 15-25h/semana para Eng. Produção.
                </p>
                {form.formState.errors.weekly_hours && (
                  <Field error={form.formState.errors.weekly_hours.message} />
                )}
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...form.register("treineira")}
                  className="mt-1 size-4 rounded border-[--color-border] accent-[color:var(--color-accent)]"
                />
                <span className="text-[length:var(--text-body-sm)] text-[color:var(--color-foreground)]">
                  Estou em <strong>regime treineira</strong> (vou apenas treinar, não disputar vaga
                  efetiva neste ano).
                </span>
              </label>

              <Button type="submit" size="lg" variant="primary" className="w-full" loading={submitting}>
                Salvar e ir para o painel FuvestPro
                <ArrowUpRight className="size-4" />
              </Button>
            </form>
          </Card>
        </div>
      </Container>
    </div>
  );
}

function Field({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1.5 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
      <AlertCircle className="size-3" />
      {error}
    </p>
  );
}
